import { PlusCircle } from "react-feather";
import { Link } from "react-router-dom";
import { Tabs, DatePicker, Modal, Table, Popconfirm, Input } from "antd";
import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import moment from "moment";
import Swal from "sweetalert2";
import AssignTraining from "../../core/modals/training/AssignTraining";
import { getAllAssignments, getAssignmentsByUserId, deleteAssignmentById } from "../../service/trainingService";

const TrainingAssignmentList = () => {
  const [openAssignTrainingModal, setOpenAssignTrainingModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [searchEmployeeName, setSearchEmployeeName] = useState("");
  const [searchProgramName, setSearchProgramName] = useState("");
  const [searchAssignmentDate, setSearchAssignmentDate] = useState(null);
  const [mySearchEmployeeName, setMySearchEmployeeName] = useState("");
  const [mySearchProgramName, setMySearchProgramName] = useState("");
  const [mySearchAssignmentDate, setMySearchAssignmentDate] = useState(null);
  const [paginationAssignments, setPaginationAssignments] = useState({ current: 1, pageSize: 5, total: 0 });
  const [paginationMyAssignments, setPaginationMyAssignments] = useState({ current: 1, pageSize: 5, total: 0 });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const pageSizeOptions = [5, 10, 20, 50, 100];

  const getUserId = () => {
    try {
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        console.error('User data not found in local storage.');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'User data not found. Please log in again.',
        });
        return null;
      }
      const user = JSON.parse(storedUserData);
      return user.data._id; // Adjust based on your localStorage structure
    } catch (error) {
      console.error('Error parsing user data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to retrieve user data.',
      });
      return null;
    }
  };

  const userId = getUserId();

  useEffect(() => {
    if (userId) {
      fetchAssignments();
    }
  }, [userId, paginationAssignments.current, paginationAssignments.pageSize, paginationMyAssignments.current, paginationMyAssignments.pageSize]);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const allData = await getAllAssignments({
        employeeName: searchEmployeeName,
        programName: searchProgramName,
        assignmentDate: searchAssignmentDate ? moment(searchAssignmentDate).format('YYYY-MM-DD') : '',
      });
      setAssignments(allData);
      setPaginationAssignments((prev) => ({ ...prev, total: allData.length }));

      const myData = await getAssignmentsByUserId(userId, {
        employeeName: mySearchEmployeeName,
        programName: mySearchProgramName,
        assignmentDate: mySearchAssignmentDate ? moment(mySearchAssignmentDate).format('YYYY-MM-DD') : '',
      });
      setMyAssignments(myData);
      setPaginationMyAssignments((prev) => ({ ...prev, total: myData.length }));
    } catch (error) {
      console.error("Error fetching assignments:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to fetch assignments.',
      });
      setAssignments([]);
      setMyAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchAssignments = async () => {
    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User not logged in.',
      });
      return;
    }
    setPaginationAssignments((prev) => ({ ...prev, current: 1 }));
    fetchAssignments();
  };

  const handleSearchMyAssignments = async () => {
    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User not logged in.',
      });
      return;
    }
    setPaginationMyAssignments((prev) => ({ ...prev, current: 1 }));
    fetchAssignments();
  };

  const handleTableChange = (pagination, type) => {
    if (type === "all") {
      setPaginationAssignments(pagination);
    } else if (type === "my") {
      setPaginationMyAssignments(pagination);
    }
  };

  const columnsAssignments = [
    {
      title: "Employee Name",
      dataIndex: "employee",
      key: "employee",
      render: (employee) => employee?.full_name || "N/A",
    },
    {
      title: "Training Program",
      dataIndex: "trainingProgram",
      key: "trainingProgram",
      render: (program) => program?.programName || "N/A",
    },
    {
      title: "Assignment Date",
      dataIndex: "assignmentDate",
      key: "assignmentDate",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      render: (record) => (
        <td className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              setSelectedAssignment(record);
              setOpenAssignTrainingModal(true);
            }}
            title="Edit training assignment"
          >
            <i data-feather="edit" className="feather-edit"></i>
          </Link>
          <Popconfirm
            title="Delete the Training Assignment"
            description="Are you sure to delete this training assignment?"
            onConfirm={async () => {
              try {
                await deleteAssignmentById(record._id);
                await fetchAssignments();
                Swal.fire({
                  icon: 'success',
                  title: 'Success',
                  text: 'Training assignment deleted successfully.',
                });
              } catch (error) {
                console.error("Error deleting assignment:", error);
                Swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: error.message || 'Failed to delete assignment.',
                });
              }
            }}
            okText="Yes"
            cancelText="No"
          >
            <Link className="confirm-text p-2" title="Delete assignment">
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </td>
      ),
    },
  ];

  if (!userId) {
    return (
      <div className="page-wrapper-lt">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="header-leave-container d-flex align-items-center">
                <div>
                  <ArrowLeft size={32} />
                </div>
                <div className="page-title">
                  <h4>Training Assignments</h4>
                  <h6>Please log in to view your assignments</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabItems = [
    {
      key: "all",
      label: "All Assignments",
      children: (
        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
            <div
              className="container mt-3"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Employee Name</label>
                  <Input
                    value={searchEmployeeName}
                    onChange={(e) => setSearchEmployeeName(e.target.value)}
                    placeholder="Enter employee name"
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Training Program</label>
                  <Input
                    value={searchProgramName}
                    onChange={(e) => setSearchProgramName(e.target.value)}
                    placeholder="Enter training program"
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Assignment Date</label>
                  <DatePicker
                    className="form-control"
                    placeholder="Select date"
                    value={searchAssignmentDate}
                    onChange={(date) => setSearchAssignmentDate(date)}
                    style={{ width: "80%", height: "40px" }}
                    format="YYYY-MM-DD"
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <button
                    disabled={loading}
                    className="btn btn-submit"
                    onClick={handleSearchAssignments}
                    style={{ height: "38px", width: "55%" }}
                  >
                    {loading ? "Searching..." : "Search"}
                  </button>
                  {loading && (
                    <div className="spinner">
                      <div className="loading-circle"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columnsAssignments}
                dataSource={assignments}
                rowKey="_id"
                pagination={{
                  current: paginationAssignments.current,
                  pageSize: paginationAssignments.pageSize,
                  pageSizeOptions: pageSizeOptions,
                  total: paginationAssignments.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={(pagination) => handleTableChange(pagination, "all")}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "my",
      label: "My Assignments",
      children: (
        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
            <div
              className="container mt-3"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Employee Name</label>
                  <Input
                    value={mySearchEmployeeName}
                    onChange={(e) => setMySearchEmployeeName(e.target.value)}
                    placeholder="Enter employee name"
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Training Program</label>
                  <Input
                    value={mySearchProgramName}
                    onChange={(e) => setMySearchProgramName(e.target.value)}
                    placeholder="Enter training program"
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Assignment Date</label>
                  <DatePicker
                    className="form-control"
                    placeholder="Select date"
                    value={mySearchAssignmentDate}
                    onChange={(date) => setMySearchAssignmentDate(date)}
                    style={{ width: "80%", height: "40px" }}
                    format="YYYY-MM-DD"
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <button
                    disabled={loading}
                    className="btn btn-submit"
                    onClick={handleSearchMyAssignments}
                    style={{ height: "38px", width: "55%" }}
                  >
                    {loading ? "Searching..." : "Search"}
                  </button>
                  {loading && (
                    <div className="spinner">
                      <div className="loading-circle"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columnsAssignments}
                dataSource={myAssignments}
                rowKey="_id"
                pagination={{
                  current: paginationMyAssignments.current,
                  pageSize: paginationMyAssignments.pageSize,
                  pageSizeOptions: pageSizeOptions,
                  total: paginationMyAssignments.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={(pagination) => handleTableChange(pagination, "my")}
              />
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container d-flex align-items-center">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Training Assignments</h4>
                <h6>Manage training assignments</h6>
              </div>
            </div>
            <div className="page-btn">
              <a
                className="btn btn-added-grid"
                onClick={() => {
                  setSelectedAssignment(null);
                  setOpenAssignTrainingModal(true);
                }}
                title="Assign new training"
              >
                <PlusCircle className="me-2" />
                Assign
              </a>
            </div>
          </div>
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
        <Modal
          zIndex={200000}
          open={openAssignTrainingModal}
          onCancel={() => setOpenAssignTrainingModal(false)}
          footer={null}
          maskClosable={false}
          closable={false}
        >
          <AssignTraining
            selectedAssignment={selectedAssignment}
            handleCancel={() => {
              setSelectedAssignment(null);
              setOpenAssignTrainingModal(false);
            }}
            handleReload={fetchAssignments}
          />
        </Modal>
      </div>
    </div>
  );
};

export default TrainingAssignmentList;